import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Zap, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { useLang } from "@/lib/i18n";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function RegisterPage() {
  const { t, lang } = useLang();
  const { user, refresh } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState("");
  const [terms, setTerms] = React.useState(false);
  const [marketing, setMarketing] = React.useState(false);

  React.useEffect(() => {
    if (user && typeof user === "object") nav(user.role === "admin" ? "/admin" : "/dashboard", { replace: true });
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!terms) {
      setErr(t("auth.consent.required"));
      return;
    }
    setBusy(true);
    try {
      // Include the required legal doc slugs so consents are recorded on the backend.
      const consents = ["terms", "privacy", "kvkk", "subscription-terms"];
      if (marketing) consents.push("marketing");
      const { data } = await api.post("/api/auth/register", {
        email, password, name, language: lang, consents,
      });
      await refresh();
      toast.success(t("auth.register.title"));
      nav("/dashboard");
    } catch (e) {
      const m = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setErr(m);
      toast.error(m);
    }
    setBusy(false);
  };

  const consentLine = (
    <span className="text-white/70 text-sm">
      {t("auth.consent.line", { links: "" })}
      <Link to="/legal/terms" className="text-[#d4af37] hover:underline mx-1" target="_blank">{t("auth.consent.terms")}</Link>·
      <Link to="/legal/privacy" className="text-[#d4af37] hover:underline mx-1" target="_blank">{t("auth.consent.privacy")}</Link>·
      <Link to="/legal/kvkk" className="text-[#d4af37] hover:underline mx-1" target="_blank">{t("auth.consent.kvkk")}</Link>·
      <Link to="/legal/subscription-terms" className="text-[#d4af37] hover:underline mx-1" target="_blank">{t("auth.consent.subscription")}</Link>
    </span>
  );

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex flex-col justify-between p-14 bg-gradient-to-br from-[#121418] to-[#0a0b0e] border-r border-white/8">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center bg-gradient-to-br from-[#d4af37] to-[#a3852c]">
            <Zap className="w-4 h-4 text-[#0a0b0e]" strokeWidth={3} />
          </div>
          <span className="font-display font-semibold text-white text-lg">LoftPress</span>
        </Link>
        <div className="max-w-md">
          <h2 className="font-display text-4xl text-white/95 leading-tight">
            {t("hero.title.a")}<br />
            <span className="text-white/60">{t("hero.title.b")}</span><br />
            <span className="lp-gold-text">{t("hero.title.c")}</span>
          </h2>
        </div>
        <div className="text-xs text-white/40 font-mono">© LoftPress · loftpress.shop</div>
      </div>

      <div className="flex items-center justify-center p-6 lg:p-14">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-md lp-card p-8"
        >
          <h1 className="font-display text-3xl text-white/95">{t("auth.register.title")}</h1>
          <p className="text-white/50 mt-2 text-sm">{t("auth.register.subtitle")}</p>
          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="register-form">
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("auth.name")}</label>
              <input required className="lp-input" value={name} onChange={(e) => setName(e.target.value)} data-testid="register-name" placeholder="Ada Lovelace" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("auth.email")}</label>
              <input type="email" required className="lp-input" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="register-email" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-white/50 mb-2 block">{t("auth.password")}</label>
              <input type="password" required minLength={8} className="lp-input" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="register-password" placeholder="••••••••" />
            </div>
            <label className="flex items-start gap-3 pt-1" data-testid="consent-terms-wrapper">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-1" data-testid="consent-terms" />
              {consentLine}
            </label>
            <label className="flex items-start gap-3 pt-1">
              <input type="checkbox" checked={marketing} onChange={(e) => setMarketing(e.target.checked)} className="mt-1" data-testid="consent-marketing" />
              <span className="text-white/60 text-sm">{t("auth.consent.marketing")}</span>
            </label>
            {err && <div className="text-red-400 text-sm" data-testid="register-error">{err}</div>}
            <button type="submit" disabled={busy} className="lp-btn-primary w-full justify-center" data-testid="register-submit">
              {busy ? t("common.loading") : t("auth.submit.register")} <ArrowRight className="w-4 h-4" />
            </button>
          </form>
          <div className="mt-5 text-sm text-white/50">
            {t("auth.have")} <Link to="/login" className="text-white hover:text-[#d4af37]" data-testid="register-to-login">{t("auth.signIn")}</Link>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
